import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";

const MembershipPage = () => {
  return (
    <>
      <style>{`
        :root {
          --green: #7ac142;
          --yellow: #ffc93b;
          --text-dark: #111827;
          --text-muted: #6b7280;
          --bg-soft: #f8fafc;
          --card-bg: #ffffff;
          --radius-lg: 36px;
          --shadow: 0 20px 60px rgba(0,0,0,.08);
          --shadow-hover: 0 30px 80px rgba(0,0,0,.12);
        }

        /* PAGE */
        .membership-page {
          background: var(--bg-soft);
          padding: 110px 0 100px;
          position: relative;
          overflow: hidden;
        }

        /* FLOATING SHAPES */
        .float-shape {
          position: absolute;
          z-index: 0;
          opacity: 0.6;
          animation: floatY 6s ease-in-out infinite;
        }

        .shape-1 {
          top: 80px;
          left: -40px;
          width: 140px;
        }

        .shape-2 {
          bottom: 120px;
          right: -60px;
          width: 180px;
          animation-delay: 1.5s;
        }

        @keyframes floatY {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-16px); }
        }

        /* HERO */
        .membership-hero {
          text-align: center;
          margin-bottom: 80px;
          position: relative;
          z-index: 2;
        }

        .membership-kicker {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: .22em;
          color: var(--green);
          margin-bottom: 14px;
        }

        .membership-kicker::before,
        .membership-kicker::after {
          content: "";
          width: 36px;
          height: 2px;
          background: var(--green);
        }

        .membership-title {
          font-size: 46px;
          font-weight: 900;
          line-height: 1.15;
          color: var(--text-dark);
          margin-bottom: 18px;
        }

        .membership-title span {
          color: var(--green);
        }

        .membership-price {
          font-size: 44px;
          font-weight: 900;
          color: var(--green);
        }

        .membership-sub {
          font-size: 15px;
          color: var(--text-muted);
          margin-top: 10px;
        }

        /* SERVICES FLEX */
        .services-flex {
          display: flex;
          flex-wrap: wrap;
          gap: 32px;
          width: 100%;
          position: relative;
          z-index: 2;
        }

        .service-flex-item {
          flex: 1 1 calc(50% - 16px);
          display: flex;
        }

        .service-flex-item.full {
          flex: 1 1 100%;
        }

        .service-card {
          background: var(--card-bg);
          border-radius: var(--radius-lg);
          padding: 36px 34px;
          box-shadow: var(--shadow);
          width: 100%;
          transition: transform .35s ease, box-shadow .35s ease;
        }

        .service-card:hover {
          transform: translateY(-10px);
          box-shadow: var(--shadow-hover);
        }

        .service-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 20px;
          font-weight: 800;
          color: var(--text-dark);
          margin-bottom: 18px;
        }

        .service-title span {
          color: var(--green);
        }

        .feature-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .feature-list li {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 14px;
          color: var(--text-muted);
          margin-bottom: 10px;
          line-height: 1.6;
        }

        /* TRIAL */
        .trial-box {
          margin-top: 80px;
          background: linear-gradient(
            180deg,
            rgba(122,193,66,.12),
            rgba(122,193,66,.05)
          );
          border-radius: var(--radius-lg);
          padding: 44px 38px;
          box-shadow: var(--shadow);
          position: relative;
          z-index: 2;
        }

        .trial-title {
          font-size: 22px;
          font-weight: 900;
          color: var(--text-dark);
          margin-bottom: 14px;
        }

        /* CTA */
        .cta-box {
          text-align: center;
          margin-top: 70px;
          position: relative;
          z-index: 2;
        }

        .join-btn {
          background: var(--green);
          color: #fff;
          font-size: 16px;
          font-weight: 800;
          padding: 16px 46px;
          border-radius: 999px;
          box-shadow: 0 18px 40px rgba(122,193,66,.45);
        }

        /* MOBILE */
        @media (max-width: 768px) {
          .membership-title { font-size: 34px; }

          .service-flex-item,
          .service-flex-item.full {
            flex: 1 1 100%;
          }
        }
      `}</style>

      <Box className="membership-page">
        {/* FLOATING SHAPES */}
        <img
          src="https://html.themehour.net/kleanix/demo/assets/img/shape/home-3-shape-1.svg"
          className="float-shape shape-1"
          alt=""
        />
        <img
          src="https://html.themehour.net/kleanix/demo/assets/img/shape/servicce-2-shape-theme2.svg"
          className="float-shape shape-2"
          alt=""
        />

        <Container maxWidth="lg">
          {/* HERO */}
          <Box className="membership-hero">
            <Box className="membership-kicker">MEMBERSHIP PLAN</Box>

            <Typography className="membership-title">
              One Membership.<br />
              <span>Every Home Service Covered.</span>
            </Typography>

            <Typography className="membership-price">
              $125 / month
            </Typography>

            <Typography className="membership-sub">
              Priority service • No surprise costs • Cancel anytime
            </Typography>
          </Box>

          {/* SERVICES */}
          <Box className="services-flex">
            <Box className="service-flex-item">
              <Box className="service-card">
                <Typography className="service-title">
                  <StarIcon sx={{ color: "var(--green)" }} />
                  HVAC <span>Services</span>
                </Typography>
                <ul className="feature-list">
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> AC tune-up before summer</li>
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> Furnace tune-up before winter</li>
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> Filter replacement every 3 months</li>
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> 20% off labor</li>
                </ul>
              </Box>
            </Box>

            <Box className="service-flex-item">
              <Box className="service-card">
                <Typography className="service-title">
                  Inside <span>Cleaning</span>
                </Typography>
                <ul className="feature-list">
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> $25/hour</li>
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> Up to 25 hours/month</li>
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> Seasonal services</li>
                </ul>
              </Box>
            </Box>

            <Box className="service-flex-item full">
              <Box className="service-card">
                <Typography className="service-title">
                  Outside <span>Cleaning</span>
                </Typography>
                <ul className="feature-list">
                  {[
                    "Leaf & weed removal",
                    "Planting & potting",
                    "Snow removal & salting",
                    "Pressure washing",
                    "Gutter cleaning",
                    "Exterior maintenance",
                  ].map((item, i) => (
                    <li key={i}>
                      <CheckCircleIcon sx={{ color: "var(--green)" }} /> {item}
                    </li>
                  ))}
                </ul>
              </Box>
            </Box>

            <Box className="service-flex-item">
              <Box className="service-card">
                <Typography className="service-title">
                  Water <span>Mitigation</span>
                </Typography>
                <ul className="feature-list">
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> Emergency flooding</li>
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> Mold remediation</li>
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> Sewer & main repair</li>
                </ul>
              </Box>
            </Box>

            <Box className="service-flex-item">
              <Box className="service-card">
                <Typography className="service-title">
                  Wishlist <span>Specials</span>
                </Typography>
                <ul className="feature-list">
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> Custom projects</li>
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> Roofing, decks, painting</li>
                  <li><CheckCircleIcon sx={{ color: "var(--green)" }} /> 10% off labor</li>
                </ul>
              </Box>
            </Box>
          </Box>

          {/* TRIAL */}
          <Box className="trial-box">
            <Typography className="trial-title">
              First Month Trial Policy
            </Typography>
            <Typography color="var(--text-muted)">
              • Reduced pricing may apply<br />
              • Continue → discounts stay active<br />
              • Cancel → regular pricing applies
            </Typography>
          </Box>

          {/* CTA */}
          <Box className="cta-box">
            <Button className="join-btn">
              Join Membership – $125 / month
            </Button>
            <Typography sx={{ fontSize: 13, color: "var(--text-muted)", mt: 1 }}>
              No contracts • Cancel anytime
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default MembershipPage;
